import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CategoryForm } from '@/components/categories/CategoryForm';
import { useCategories, useCreateCategory } from '@/hooks/useCategories';
import { usePots } from '@/hooks/usePots';
import { useTranslation } from '@/i18n';
import type { RecurringTransaction, CreateRecurringInput, RecurringFrequency } from '@/types/recurring';
import type { Category, CreateCategoryInput } from '@/types/category';

interface RecurringFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRecurringInput) => void;
  recurring?: RecurringTransaction | null;
  isLoading?: boolean;
}

export const RecurringForm: React.FC<RecurringFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  recurring,
  isLoading = false,
}) => {
  const { t, locale } = useTranslation();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: potsData } = usePots();
  const createCategoryMutation = useCreateCategory();
  const categories = categoriesData?.categories || [];
  const pots = potsData?.pots || [];
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const handleCreateCategory = async (data: CreateCategoryInput) => {
    const newCategory = await createCategoryMutation.mutateAsync(data);
    setShowCategoryForm(false);
    // Auto-select the new category
    setFormData((prev) => ({ ...prev, categoryId: newCategory.id }));
  };

  // Get localized category name
  const getCategoryName = (category: Category): string => {
    if (locale === 'pt-BR') {
      return category.name;
    }
    return category.nameEn || category.name;
  };

  // Format currency based on locale
  const formatCurrency = (value: number) => {
    const currencyMap: Record<string, string> = {
      'pt-BR': 'BRL',
      'en-GB': 'GBP',
      'es-ES': 'EUR',
      'fr-FR': 'EUR',
      'it-IT': 'EUR',
      'ar': 'SAR',
      'zh-CN': 'CNY',
    };
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyMap[locale] || 'GBP',
    }).format(value);
  };

  const [formData, setFormData] = useState<CreateRecurringInput>({
    type: 'expense',
    categoryId: '',
    potId: null,
    amount: 0,
    description: '',
    frequency: 'monthly',
    dayOfMonth: 1,
    dayOfWeek: null,
    startDate: new Date().toISOString().split('T')[0],
    endDate: null,
  });

  useEffect(() => {
    if (recurring) {
      setFormData({
        type: recurring.type,
        categoryId: recurring.categoryId,
        potId: recurring.potId || null,
        amount: recurring.amount,
        description: recurring.description,
        frequency: recurring.frequency,
        dayOfMonth: recurring.dayOfMonth,
        dayOfWeek: recurring.dayOfWeek,
        startDate: recurring.startDate.split('T')[0],
        endDate: recurring.endDate?.split('T')[0] || null,
      });
    } else {
      setFormData({
        type: 'expense',
        categoryId: '',
        potId: null,
        amount: 0,
        description: '',
        frequency: 'monthly',
        dayOfMonth: 1,
        dayOfWeek: null,
        startDate: new Date().toISOString().split('T')[0],
        endDate: null,
      });
    }
  }, [recurring, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Find the selected category to ensure it's included in options when editing
  const selectedCategory = categories.find((c) => c.id === formData.categoryId);

  const daysOfMonth = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: `${t.app.recurring.form.day} ${i + 1}`,
  }));

  const daysOfWeek = [
    { value: 1, label: t.app.recurring.form.daysOfWeek.monday },
    { value: 2, label: t.app.recurring.form.daysOfWeek.tuesday },
    { value: 3, label: t.app.recurring.form.daysOfWeek.wednesday },
    { value: 4, label: t.app.recurring.form.daysOfWeek.thursday },
    { value: 5, label: t.app.recurring.form.daysOfWeek.friday },
    { value: 6, label: t.app.recurring.form.daysOfWeek.saturday },
    { value: 0, label: t.app.recurring.form.daysOfWeek.sunday },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={recurring ? t.app.recurring.form.editRecurring : t.app.recurring.form.newRecurring}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, type: 'expense' })}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              formData.type === 'expense'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-400'
            }`}
          >
            {t.app.common.expense}
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, type: 'income' })}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              formData.type === 'income'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-400'
            }`}
          >
            {t.app.common.income}
          </button>
        </div>

        {/* Description & Amount - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t.app.recurring.form.description}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t.app.recurring.form.descriptionPlaceholder}
            required
          />
          <Input
            label={t.app.recurring.form.amount}
            type="number"
            min={0}
            step={0.01}
            value={formData.amount || ''}
            onChange={(e) =>
              setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
            }
            placeholder="0.00"
            required
          />
        </div>

        {/* Category & Pot - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.app.recurring.form.category}
            </label>
            <div className="flex gap-2">
              <Select
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                disabled={categoriesLoading}
                options={[
                  { value: '', label: categoriesLoading ? t.app.common.loading : t.app.recurring.form.selectCategory },
                  ...(selectedCategory && !categories.find(c => c.id === selectedCategory.id)
                    ? [{ value: selectedCategory.id, label: getCategoryName(selectedCategory) }]
                    : []),
                  ...categories.map((c) => ({ value: c.id, label: getCategoryName(c) })),
                ]}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => setShowCategoryForm(true)}
                className="p-2 rounded-lg border border-gray-300 dark:border-secondary-600 hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                title={t.app.common.create}
              >
                <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
          <Select
            label={t.app.sidebar.pots}
            value={formData.potId || ''}
            onChange={(e) => setFormData({ ...formData, potId: e.target.value || null })}
            options={[
              { value: '', label: t.app.recurring.form.selectPot },
              ...pots.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </div>

        {/* Frequency & Day - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label={t.app.recurring.form.frequencyLabel}
            value={formData.frequency}
            onChange={(e) => {
              const frequency = e.target.value as RecurringFrequency;
              setFormData({
                ...formData,
                frequency,
                dayOfMonth: frequency === 'monthly' ? formData.dayOfMonth || 1 : null,
                dayOfWeek: frequency === 'weekly' ? formData.dayOfWeek || 1 : null,
              });
            }}
            options={[
              { value: 'daily', label: t.app.recurring.frequency.daily },
              { value: 'weekly', label: t.app.recurring.frequency.weekly },
              { value: 'monthly', label: t.app.recurring.frequency.monthly },
              { value: 'yearly', label: t.app.recurring.frequency.yearly },
            ]}
          />
          {formData.frequency === 'monthly' && (
            <Select
              label={t.app.recurring.form.dayOfMonth}
              value={formData.dayOfMonth?.toString() || '1'}
              onChange={(e) =>
                setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) })
              }
              options={daysOfMonth.map((d) => ({ value: d.value.toString(), label: d.label }))}
            />
          )}
          {formData.frequency === 'weekly' && (
            <Select
              label={t.app.recurring.form.dayOfWeek}
              value={formData.dayOfWeek?.toString() || '1'}
              onChange={(e) =>
                setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })
              }
              options={daysOfWeek.map((d) => ({ value: d.value.toString(), label: d.label }))}
            />
          )}
          {(formData.frequency === 'daily' || formData.frequency === 'yearly') && (
            <div /> // Empty space to maintain grid layout
          )}
        </div>

        {/* Dates - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t.app.recurring.form.startDate}
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            required
          />
          <Input
            label={t.app.recurring.form.endDate}
            type="date"
            value={formData.endDate || ''}
            onChange={(e) =>
              setFormData({ ...formData, endDate: e.target.value || null })
            }
          />
        </div>

        {/* Preview */}
        <div className="p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {formData.description || t.app.recurring.form.description}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.app.recurring.frequency[formData.frequency]}
                {formData.frequency === 'monthly' && ` - ${t.app.recurring.form.day} ${formData.dayOfMonth}`}
                {formData.frequency === 'weekly' && ` - ${daysOfWeek.find(d => d.value === formData.dayOfWeek)?.label}`}
              </p>
            </div>
            <p className={`text-xl font-bold ${
              formData.type === 'income'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}>
              {formData.type === 'income' ? '+' : '-'}{formatCurrency(formData.amount || 0)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-200 dark:border-secondary-700">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t.app.common.cancel}
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {recurring ? t.app.recurring.form.save : t.app.recurring.form.create}
          </Button>
        </div>
      </form>

      {/* Create Category Modal */}
      <CategoryForm
        isOpen={showCategoryForm}
        onClose={() => setShowCategoryForm(false)}
        onSubmit={handleCreateCategory}
        isLoading={createCategoryMutation.isPending}
      />
    </Modal>
  );
};

export default RecurringForm;
